import React, { useState, useEffect, useMemo } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import { app } from '../firebase/config';

const CADashboard = () => {
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [showStateAnalysis, setShowStateAnalysis] = useState(false);
  const [showDistrictAnalysis, setShowDistrictAnalysis] = useState(false);

  useEffect(() => {
    const db = getDatabase(app);
    const ordersRef = ref(db, 'HTAMS/orders');

    const unsubscribeOrders = onValue(ordersRef, (snapshot) => {
      const ordersList = [];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const order = childSnapshot.val();
          ordersList.push({
            id: childSnapshot.key,
            ...order,
            orderDate: order.orderDate || order.createdAt || new Date().toISOString()
          });
        });
      }
      setOrders(ordersList);
      setLoading(false);
    });

    return () => {
      unsubscribeOrders();
    };
  }, []);

  // Get available months from orders
  const availableMonths = useMemo(() => {
    const monthsSet = new Set();
    orders.forEach(order => {
      const date = new Date(order.orderDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      monthsSet.add(monthKey);
    });
    return Array.from(monthsSet).sort().reverse();
  }, [orders]);

  const filteredData = useMemo(() => {
    if (selectedMonth === 'all') {
      return orders;
    }

    const [year, month] = selectedMonth.split('-');
    return orders.filter(order => {
      const orderDate = new Date(order.orderDate);
      return orderDate.getFullYear() === parseInt(year) &&
        orderDate.getMonth() === parseInt(month) - 1;
    });
  }, [orders, selectedMonth]);

  const analytics = useMemo(() => {
    const totalOrders = filteredData.length;
    const totalRevenue = filteredData.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

    // Daily data for line chart (last 30 days or selected month)
    const dailyData = [];
    const daysToShow = selectedMonth === 'all' ? 30 : 31;
    const endDate = new Date();

    for (let i = daysToShow - 1; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);

      if (selectedMonth !== 'all') {
        const [year, month] = selectedMonth.split('-');
        date.setFullYear(parseInt(year));
        date.setMonth(parseInt(month) - 1);
        date.setDate(i + 1);
      }

      date.setHours(0, 0, 0, 0);
      const nextDate = new Date(date);
      nextDate.setDate(nextDate.getDate() + 1);

      const dayOrders = orders.filter(order => {
        const orderDate = new Date(order.orderDate);
        return orderDate >= date && orderDate < nextDate;
      });

      dailyData.push({
        date: date.getDate(),
        monthDay: date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
        orders: dayOrders.length,
        revenue: dayOrders.reduce((sum, o) => sum + (o.totalAmount || 0), 0)
      });
    }

    // Category-wise revenue
    const categoryRevenue = filteredData.reduce((acc, order) => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          const category = item.category || 'Uncategorized';
          if (!acc[category]) acc[category] = 0;
          acc[category] += item.totalPrice || item.total || 0;
        });
      }
      return acc;
    }, {});

    const topCategories = Object.entries(categoryRevenue)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    // Product sales
    const productSales = filteredData.reduce((acc, order) => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          const name = item.productName || item.name || 'Unknown';
          if (!acc[name]) acc[name] = { count: 0, revenue: 0 };
          acc[name].count += item.quantity || 1;
          acc[name].revenue += item.totalPrice || item.total || 0;
        });
      }
      return acc;
    }, {});

    const topProducts = Object.entries(productSales)
      .sort((a, b) => b[1].revenue - a[1].revenue)
      .slice(0, 8);

    const recentOrders = [...filteredData]
      .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
      .slice(0, 8);

    // State breakdown
    const stateBreakdown = filteredData.reduce((acc, order) => {
      const state = order.customerAddress?.state || order.invoice?.customerState || 'Unknown';
      if (!acc[state]) acc[state] = { count: 0, revenue: 0 };
      acc[state].count++;
      acc[state].revenue += order.totalAmount || 0;
      return acc;
    }, {});

    // District breakdown
    const districtBreakdown = filteredData.reduce((acc, order) => {
      const district = order.customerAddress?.city || order.district || 'Unknown';
      const state = order.customerAddress?.state || order.invoice?.customerState || 'Unknown';
      if (!acc[district]) acc[district] = { count: 0, revenue: 0, state };
      acc[district].count++;
      acc[district].revenue += order.totalAmount || 0;
      return acc;
    }, {});

    // Status breakdown
    const statusBreakdown = filteredData.reduce((acc, order) => {
      const status = order.status || 'Unknown';
      if (!acc[status]) acc[status] = { count: 0, revenue: 0 };
      acc[status].count++;
      acc[status].revenue += order.totalAmount || 0;
      return acc;
    }, {});

    return {
      totalOrders,
      totalRevenue,
      avgOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
      dailyData,
      topCategories,
      topProducts,
      recentOrders,
      stateBreakdown,
      districtBreakdown,
      statusBreakdown
    };
  }, [filteredData, orders, selectedMonth]);

  const getMonthName = (monthKey) => {
    if (monthKey === 'all') return 'All Months';
    const [year, month] = monthKey.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px'
          }}></div>
          <p style={{ color: '#64748b', margin: 0 }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="ca-dashboard-compact">
      {/* Month Filter */}
      <div className="filter-section">
        <label>Filter by Month:</label>
        <select
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="month-select"
        >
          <option value="all">All Months</option>
          {availableMonths.map(month => (
            <option key={month} value={month}>
              {getMonthName(month)}
            </option>
          ))}
        </select>
      </div>

      {/* Compact Stats - Only Revenue & Orders */}
      <div className="stats-compact">
        <div className="stat-box">
          <div className="stat-label">TOTAL REVENUE</div>
          <div className="stat-big">₹{analytics.totalRevenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
        </div>
        <div className="stat-box">
          <div className="stat-label">TOTAL ORDERS</div>
          <div className="stat-big">{analytics.totalOrders.toLocaleString('en-IN')}</div>
          <div className="stat-small">Avg: ₹{analytics.avgOrderValue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
        </div>
      </div>

      {/* Breakdowns Section */}
      <div className="breakdowns-section">
        {/* Status Breakdown */}
        <div className="breakdown-card">
          <div className="breakdown-header">Status Breakdown</div>
          <div className="breakdown-items">
            {Object.entries(analytics.statusBreakdown).map(([status, data]) => (
              <div key={status} className="breakdown-row">
                <span className={`status-indicator ${status.toLowerCase()}`}></span>
                <span className="breakdown-label">{status}</span>
                <span className="breakdown-count">{data.count}</span>
                <span className="breakdown-value">₹{data.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Products */}
        <div className="breakdown-card">
          <div className="breakdown-header">Top Products</div>
          <div className="breakdown-items">
            {analytics.topProducts.slice(0, 5).map(([product, data], index) => (
              <div key={product} className="breakdown-row">
                <span className="product-rank">#{index + 1}</span>
                <span className="breakdown-label">{product}</span>
                <span className="breakdown-count">{data.count} units</span>
                <span className="breakdown-value">₹{data.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Line Chart for Revenue Trend */}
        <div className="chart-box">
          <div className="chart-title">Revenue Trend</div>
          <div className="line-chart">
            <svg width="100%" height="180" style={{ overflow: 'visible' }}>
              {analytics.dailyData.length > 0 && (() => {
                const maxRevenue = Math.max(...analytics.dailyData.map(d => d.revenue), 1);
                const width = 100;
                const height = 150;
                const points = analytics.dailyData.map((d, i) => {
                  const x = (i / (analytics.dailyData.length - 1)) * 100;
                  const y = height - (d.revenue / maxRevenue) * height;
                  return `${x},${y}`;
                }).join(' ');

                const areaPoints = `0,${height} ${points} 100,${height}`;

                return (
                  <>
                    <defs>
                      <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0.3 }} />
                        <stop offset="100%" style={{ stopColor: '#3b82f6', stopOpacity: 0 }} />
                      </linearGradient>
                    </defs>
                    <polygon points={areaPoints} fill="url(#areaGradient)" />
                    <polyline
                      points={points}
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                    />
                    {analytics.dailyData.map((d, i) => {
                      const x = (i / (analytics.dailyData.length - 1)) * 100;
                      const y = height - (d.revenue / maxRevenue) * height;
                      return (
                        <circle
                          key={i}
                          cx={x}
                          cy={y}
                          r="3"
                          fill="#3b82f6"
                        />
                      );
                    })}
                  </>
                );
              })()}
            </svg>
            <div className="chart-labels">
              {analytics.dailyData.filter((_, i) => i % Math.ceil(analytics.dailyData.length / 7) === 0).map((d, i) => (
                <span key={i}>{d.monthDay}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Category Revenue Chart */}
        <div className="chart-box">
          <div className="chart-title">Category Revenue</div>
          <div className="category-chart">
            {analytics.topCategories.length > 0 ? (
              analytics.topCategories.map(([category, revenue], index) => {
                const maxRevenue = analytics.topCategories[0][1];
                const percentage = (revenue / maxRevenue) * 100;
                return (
                  <div key={index} className="category-item">
                    <div className="category-info">
                      <span className="category-name">{category}</span>
                      <span className="category-value">₹{revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</span>
                    </div>
                    <div className="category-bar-bg">
                      <div
                        className="category-bar"
                        style={{
                          width: `${percentage}%`,
                          background: `hsl(${220 - index * 20}, 90%, 55%)`
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="no-data-small">No category data</div>
            )}
          </div>
        </div>
      </div>

      {/* Data Tables */}
      <div className="tables-section">
        {/* Top Products */}
        <div className="table-box">
          <div className="table-title">Top Products</div>
          <div className="table-content">
            {analytics.topProducts.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>Units</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.topProducts.map(([name, data], index) => (
                    <tr key={index}>
                      <td className="rank">{index + 1}</td>
                      <td className="product-name">{name}</td>
                      <td>{data.count}</td>
                      <td className="revenue">₹{data.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data-small">No products</div>
            )}
          </div>
        </div>

        {/* Recent Orders */}
        <div className="table-box">
          <div className="table-title">Recent Orders</div>
          <div className="table-content">
            {analytics.recentOrders.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Customer</th>
                    <th>Date</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.recentOrders.map((order, index) => (
                    <tr key={index}>
                      <td className="order-id">{order.id?.substring(0, 8)}...</td>
                      <td>{order.customerName || 'N/A'}</td>
                      <td className="date">{new Date(order.orderDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                      <td className="revenue">₹{(order.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data-small">No orders</div>
            )}
          </div>
        </div>
      </div>

      {/* Geographic Analysis Sections */}
      <div className="geo-analysis-sections">
        {/* State Analysis */}
        <div className="geo-box">
          <div
            className="geo-header"
            onClick={() => setShowStateAnalysis(!showStateAnalysis)}
          >
            <span>State-wise Analysis (Top 5)</span>
            <span className="toggle-arrow">{showStateAnalysis ? '▼' : '▶'}</span>
          </div>
          {showStateAnalysis && (
            <div className="geo-content">
              <table>
                <thead>
                  <tr>
                    <th>State</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                    <th>% Total</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analytics.stateBreakdown)
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .slice(0, 5)
                    .map(([state, data]) => (
                      <tr key={state}>
                        <td className="td-bold">{state}</td>
                        <td>{data.count}</td>
                        <td className="td-revenue">₹{data.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                        <td className="td-percent">{((data.revenue / analytics.totalRevenue) * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* District Analysis */}
        <div className="geo-box">
          <div
            className="geo-header"
            onClick={() => setShowDistrictAnalysis(!showDistrictAnalysis)}
          >
            <span>District-wise Analysis (Top 10)</span>
            <span className="toggle-arrow">{showDistrictAnalysis ? '▼' : '▶'}</span>
          </div>
          {showDistrictAnalysis && (
            <div className="geo-content">
              <table>
                <thead>
                  <tr>
                    <th>District</th>
                    <th>State</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(analytics.districtBreakdown)
                    .sort((a, b) => b[1].revenue - a[1].revenue)
                    .slice(0, 10)
                    .map(([district, data]) => (
                      <tr key={district}>
                        <td className="td-bold">{district}</td>
                        <td className="td-muted">{data.state}</td>
                        <td>{data.count}</td>
                        <td className="td-revenue">₹{data.revenue.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .ca-dashboard-compact {
          max-width: 100%;
          margin: 0 auto;
        }

        .filter-section {
          background: white;
          padding: 10px 16px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .filter-section label {
          font-size: 0.85rem;
          font-weight: 600;
          color: #475569;
        }

        .month-select {
          padding: 6px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 4px;
          font-size: 0.85rem;
          background: white;
          cursor: pointer;
          min-width: 200px;
        }

        .month-select:focus {
          outline: none;
          border-color: #3b82f6;
        }

        .stats-compact {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .stat-box {
          background: white;
          padding: 14px 18px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .stat-label {
          font-size: 0.7rem;
          font-weight: 700;
          color: #64748b;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .stat-big {
          font-size: 1.75rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 2px;
        }

        .stat-small {
          font-size: 0.75rem;
          color: #64748b;
        }

        /* Breakdowns Section */
        .breakdowns-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .breakdown-card {
          background: white;
          padding: 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .breakdown-header {
          font-size: 0.85rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .breakdown-items {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .breakdown-row {
          display: grid;
          grid-template-columns: auto 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 8px;
          background: #f8fafc;
          border-radius: 4px;
          font-size: 0.8rem;
        }

        .status-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .status-indicator.pending { background: #f59e0b; }
        .status-indicator.completed { background: #10b981; }
        .status-indicator.cancelled { background: #ef4444; }
        .status-indicator.unknown { background: #94a3b8; }

        .product-rank {
          font-weight: 700;
          color: #3b82f6;
          font-size: 0.85rem;
        }

        .breakdown-label {
          font-weight: 600;
          color: #1e293b;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .breakdown-count {
          color: #64748b;
          font-size: 0.75rem;
          text-align: right;
        }

        .breakdown-value {
          font-weight: 700;
          color: #10b981;
          text-align: right;
        }

        .charts-section {
          display: grid;
          grid-template-columns: 1.2fr 0.8fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .chart-box {
          background: white;
          padding: 14px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .chart-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .line-chart {
          position: relative;
        }

        .chart-labels {
          display: flex;
          justify-content: space-between;
          margin-top: 8px;
          font-size: 0.65rem;
          color: #64748b;
        }

        .category-chart {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .category-item {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .category-info {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
        }

        .category-name {
          font-weight: 600;
          color: #1e293b;
        }

        .category-value {
          font-weight: 700;
          color: #64748b;
        }

        .category-bar-bg {
          height: 6px;
          background: #f1f5f9;
          border-radius: 3px;
          overflow: hidden;
        }

        .category-bar {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s;
        }

        .tables-section {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .table-box {
          background: white;
          padding: 14px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        }

        .table-title {
          font-size: 0.85rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .table-content {
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.8rem;
        }

        thead {
          background: #f8fafc;
        }

        th {
          text-align: left;
          padding: 8px 10px;
          font-weight: 700;
          color: #475569;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.3px;
        }

        td {
          padding: 8px 10px;
          color: #1e293b;
          border-bottom: 1px solid #f1f5f9;
        }

        tbody tr:hover {
          background: #f8fafc;
        }

        .rank {
          font-weight: 700;
          color: #3b82f6;
          width: 30px;
        }

        .product-name {
          font-weight: 600;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .order-id {
          font-family: monospace;
          font-size: 0.75rem;
          color: #64748b;
        }

        .date {
          color: #64748b;
          font-size: 0.75rem;
        }

        .revenue {
          font-weight: 700;
          color: #10b981;
        }

        .no-data-small {
          text-align: center;
          padding: 20px;
          color: #94a3b8;
          font-size: 0.8rem;
        }

        /* Geographic Analysis */
        .geo-analysis-sections {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 16px;
        }

        .geo-box {
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
          overflow: hidden;
        }

        .geo-header {
          padding: 12px 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 700;
          color: #1e293b;
          background: white;
          transition: background 0.2s;
        }

        .geo-header:hover {
          background: #f8fafc;
        }

        .toggle-arrow {
          font-size: 0.7rem;
          color: #3b82f6;
        }

        .geo-content {
          padding: 12px;
          border-top: 1px solid #e2e8f0;
          overflow-x: auto;
        }

        .td-bold {
          font-weight: 600;
          color: #1e293b;
        }

        .td-muted {
          color: #94a3b8;
          font-size: 0.75rem;
        }

        .td-revenue {
          font-weight: 700;
          color: #10b981;
        }

        .td-percent {
          color: #3b82f6;
          font-weight: 600;
        }

        @media (max-width: 1024px) {
          .charts-section {
            grid-template-columns: 1fr;
          }
          .tables-section {
            grid-template-columns: 1fr;
          }
          .breakdowns-section {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .stats-compact {
            grid-template-columns: 1fr;
          }
          .stat-big {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CADashboard;
